import type { ReactNode } from "react";
import { useAuth } from "@clerk/clerk-react";
import { Navigate } from "@tanstack/react-router";
import { useCurrentUser } from "@/lib/me";
import { NotFound } from "./NotFound";
import styles from "./StaffGuard.module.scss";

interface Props {
  children: ReactNode;
}

/**
 * Gate a subtree on staff status. Non-staff Clerk accounts see a 404
 * (intentionally — we don't want to leak that `/staff` exists). Signed-out
 * users get redirected to `/sign-in`.
 */
export function StaffGuard({ children }: Props) {
  const { isLoaded: clerkLoaded, isSignedIn } = useAuth();
  const { data, isLoading, isError } = useCurrentUser();

  if (!clerkLoaded) return <Loading />;
  if (!isSignedIn) return <Navigate to="/sign-in" />;
  if (isLoading) return <Loading />;
  if (isError || !data) return <NotFound />;
  if (!data.isAdmin) return <NotFound />;

  return <>{children}</>;
}

function Loading() {
  return (
    <div className={styles.loading} role="status" aria-live="polite">
      Loading…
    </div>
  );
}
